import React, { useState, useEffect } from 'react';
import { X, Save, User, Briefcase, Plus, Trash2, CheckCircle, AlertTriangle } from 'lucide-react';
import { rattachementsService, projetsService } from '../services/api';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const RattachementModal = ({ 
  isOpen, 
  onClose, 
  personne, 
  onRattachementsUpdated
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState({});
  const [existingRattachements, setExistingRattachements] = useState([]);
  const [availableProjets, setAvailableProjets] = useState([]);
  
  // Form state pour le nouveau rattachement
  const [selectedProjet, setSelectedProjet] = useState('');
  const [roleProjet, setRoleProjet] = useState('');
  const [phase, setPhase] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  // Options de rôles
  const rolesOptions = [
    { value: 'chef_projet', label: 'Chef de projet' },
    { value: 'developpeur', label: 'Développeur' },
    { value: 'designer', label: 'Designer' },
    { value: 'analyste', label: 'Analyste' },
    { value: 'testeur', label: 'Testeur' },
    { value: 'consultant', label: 'Consultant' }
  ];

  // Options de phases
  const phasesOptions = [
    { value: 'analyse_design', label: 'Analyse & Design' },
    { value: 'build', label: 'Développement' },
    { value: 'test', label: 'Test' }
  ];

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen && personne) {
      resetForm();
      loadData();
    }
  }, [isOpen, personne]);

  const resetForm = () => {
    setSelectedProjet('');
    setRoleProjet('');
    setPhase('');
    setDateDebut('');
    setDateFin('');
    setErrors({});
    setSaved(false);
    setExistingRattachements([]);
    setAvailableProjets([]);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Charger tous les projets et rattachements en parallèle
      const [projetsResponse, rattachementsResponse] = await Promise.all([
        projetsService.getAll(),
        rattachementsService.getAll()
      ]);
      
      const projets = projetsResponse.data || [];
      const allRattachements = rattachementsResponse.data || [];
      
      // Filtrer les rattachements de cette personne
      const personneRattachements = allRattachements
        .filter(r => r.id_personne === personne.id && r.actif)
        .map(r => {
          const projet = projets.find(p => p.id === r.id_projet);
          return {
            ...r,
            projet
          };
        });
      
      setExistingRattachements(personneRattachements);
      setAvailableProjets(projets);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setErrors({ load: 'Erreur lors du chargement des données' });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedProjet) {
      newErrors.projet = 'Sélectionnez un projet';
    }
    
    if (!roleProjet) {
      newErrors.role = 'Sélectionnez un rôle';
    }
    
    // Vérifier si la personne n'est pas déjà rattachée à ce projet
    const alreadyAttached = existingRattachements.some(r => r.id_projet.toString() === selectedProjet);
    if (alreadyAttached) {
      newErrors.projet = 'Cette personne est déjà rattachée à ce projet';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      
      const rattachementData = {
        id_projet: parseInt(selectedProjet),
        id_personne: personne.id,
        role_projet: roleProjet,
        phase: phase || null,
        date_debut: dateDebut || null,
        date_fin: dateFin || null,
        actif: true
      };

      await rattachementsService.create(rattachementData);
      
      setSaved(true);
      await loadData(); // Recharger les données
      
      // Reset form après ajout
      setSelectedProjet('');
      setRoleProjet('');
      setPhase('');
      setDateDebut('');
      setDateFin('');
      
      if (onRattachementsUpdated) {
        onRattachementsUpdated();
      }
      
      setTimeout(() => {
        setSaved(false);
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du rattachement:', error);
      setErrors({ save: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRattachement = async (rattachementId) => {
    try {
      await rattachementsService.delete(rattachementId);
      await loadData(); // Recharger les données
      
      if (onRattachementsUpdated) {
        onRattachementsUpdated();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du rattachement:', error);
      setErrors({ delete: 'Erreur lors de la suppression' });
    }
  };

  const getProjetStatusColor = (statut) => {
    switch (statut) {
      case 'planifie': return 'text-blue-600 bg-blue-50';
      case 'en_cours': return 'text-green-600 bg-green-50';
      case 'termine': return 'text-gray-600 bg-gray-50';
      case 'suspendu': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getProjetStatusLabel = (statut) => {
    switch (statut) {
      case 'planifie': return 'Planifié';
      case 'en_cours': return 'En cours';
      case 'termine': return 'Terminé';
      case 'suspendu': return 'Suspendu';
      default: return statut;
    }
  };

  const getRoleLabel = (role) => {
    const roleOption = rolesOptions.find(r => r.value === role);
    return roleOption ? roleOption.label : role;
  };

  const getPhaseLabel = (phase) => {
    const phaseOption = phasesOptions.find(p => p.value === phase);
    return phaseOption ? phaseOption.label : phase;
  };

  // Filtrer les projets disponibles (exclure ceux déjà rattachés et les terminés)
  const projetsDisponibles = availableProjets.filter(projet => {
    const alreadyAttached = existingRattachements.some(r => r.id_projet === projet.id);
    return !alreadyAttached && projet.statut !== 'termine';
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <Briefcase className="h-5 w-5 mr-2 text-primary-600" />
                Gestion des rattachements
              </h2>
              {personne && (
                <p className="text-sm text-gray-600 mt-1 flex items-center">
                  <User className="h-4 w-4 mr-1" />
                  {personne.prenom} {personne.nom}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {saved && (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="h-5 w-5 mr-1" />
                  <span className="text-sm font-medium">Sauvegardé</span>
                </div>
              )}
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          )}

          {/* Erreurs de chargement */}
          {errors.load && (
            <div className="mb-6 flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{errors.load}</span>
            </div>
          )}

          {!loading && (
            <>
              {/* Rattachements existants */}
              {existingRattachements.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Projets actuels ({existingRattachements.length})</h3>
                  <div className="space-y-3">
                    {existingRattachements.map((rattachement) => (
                      <div key={rattachement.id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-4 flex-1">
                          <Briefcase className="h-5 w-5 text-gray-400" />
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <span className="font-medium text-gray-900">
                                {rattachement.projet?.nom || 'Projet inconnu'}
                              </span>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getProjetStatusColor(rattachement.projet?.statut)}`}>
                                {getProjetStatusLabel(rattachement.projet?.statut)}
                              </span>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {getRoleLabel(rattachement.role_projet)}
                              </span>
                              {rattachement.phase && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {getPhaseLabel(rattachement.phase)}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {rattachement.date_debut && (
                                <span>
                                  Depuis le {format(parseISO(rattachement.date_debut), 'dd MMMM yyyy', { locale: fr })}
                                </span>
                              )}
                              {rattachement.date_fin && (
                                <span>
                                  {rattachement.date_debut ? ' jusqu\'au ' : 'Jusqu\'au '}
                                  {format(parseISO(rattachement.date_fin), 'dd MMMM yyyy', { locale: fr })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteRattachement(rattachement.id)}
                          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-md transition-colors"
                          title="Supprimer ce rattachement"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulaire d'ajout */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Plus className="h-5 w-5 mr-2 text-primary-600" />
                  Ajouter un nouveau rattachement
                </h3>

                {projetsDisponibles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Briefcase className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-lg font-medium">Aucun projet disponible</p>
                    <p className="text-sm">Tous les projets actifs sont déjà rattachés à cette personne.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Sélection du projet, rôle et phase */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="form-label">Projet *</label>
                        <select
                          value={selectedProjet}
                          onChange={(e) => setSelectedProjet(e.target.value)}
                          className={`form-input ${errors.projet ? 'border-red-500' : ''}`}
                        >
                          <option value="">Sélectionnez un projet</option>
                          {projetsDisponibles.map((projet) => (
                            <option key={projet.id} value={projet.id}>
                              {projet.nom} ({getProjetStatusLabel(projet.statut)})
                            </option>
                          ))}
                        </select>
                        {errors.projet && (
                          <p className="mt-1 text-sm text-red-600">{errors.projet}</p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">Rôle sur le projet *</label>
                        <select
                          value={roleProjet}
                          onChange={(e) => setRoleProjet(e.target.value)}
                          className={`form-input ${errors.role ? 'border-red-500' : ''}`}
                        >
                          <option value="">Sélectionnez un rôle</option>
                          {rolesOptions.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                        {errors.role && (
                          <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">Phase du projet</label>
                        <select
                          value={phase}
                          onChange={(e) => setPhase(e.target.value)}
                          className="form-input"
                        >
                          <option value="">Toutes les phases</option>
                          {phasesOptions.map((phaseOption) => (
                            <option key={phaseOption.value} value={phaseOption.value}>
                              {phaseOption.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Date de début (optionnel)</label>
                        <input
                          type="date"
                          value={dateDebut}
                          onChange={(e) => setDateDebut(e.target.value)}
                          className="form-input"
                        />
                      </div>

                      <div>
                        <label className="form-label">Date de fin (optionnel)</label>
                        <input
                          type="date"
                          value={dateFin}
                          onChange={(e) => setDateFin(e.target.value)}
                          className="form-input"
                        />
                      </div>
                    </div>

                    {/* Erreur de sauvegarde */}
                    {errors.save && (
                      <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                        <span className="text-red-700">{errors.save}</span>
                      </div>
                    )}

                    {/* Bouton d'ajout */}
                    <div className="flex justify-end">
                      <button 
                        onClick={handleSave}
                        disabled={saving || !selectedProjet || !roleProjet}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {saving ? 'Ajout...' : 'Ajouter le rattachement'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RattachementModal;
